// src/components/WhatsAppCampaignsTable/WhatsAppCampaignsTable.js
import React, { useState, useMemo } from "react";
import {
  Box,
  Paper,
  Toolbar,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Checkbox,
  Button,
  Tooltip,
  IconButton,
  TextField,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import InsightsIcon from "@mui/icons-material/BarChart";
import DeleteIcon from "@mui/icons-material/Delete";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CancelScheduleSendIcon from "@mui/icons-material/CancelScheduleSend";

const formatUtc = (iso) => {
  if (!iso) return "–";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { timeZone: "UTC" });
  } catch {
    return iso;
  }
};

const prettyMode = (v) => {
  if (!v) return "-";
  return String(v)
    .split("_")
    .map(s => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
    .join(" ");
};

export default React.memo(function WhatsAppCampaignsTable({
  campaigns = [],
  onEditCampaign,
  onRunCampaign,
  onInsightsCampaign,
  onDeleteCampaigns,
  page,
  rowsPerPage,
  setPage,
  setRowsPerPage,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  // Filter campaigns by search query
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return campaigns;
    return campaigns.filter((c) =>
      (c.campaign_name || "").toLowerCase().includes(q) ||
      (c.template?.template_name || "").toLowerCase().includes(q)
    );
  }, [campaigns, searchQuery]);

  // Determine selected campaigns
  const selected = filtered.filter((c) => selectedIds.includes(c.campaign_id));
  const selectedOne = selected.length === 1 ? selected[0] : null;
  const isSentSelected = selectedOne?.status?.toLowerCase() === "sent";
  const isScheduledSelected =
    !!selectedOne && selectedOne.status?.toLowerCase?.() === "scheduled";
  // Selection handlers
  const handleSelectAll = (e) =>
    setSelectedIds(e.target.checked ? filtered.map((c) => c.campaign_id) : []);
  const handleSelectOne = (id) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  // Action handlers
  const doEdit = () => {
    if (selectedOne && !isSentSelected) onEditCampaign(selectedOne);
  };
  const doSend = () => {
    if (selectedOne && !isSentSelected) onRunCampaign(selectedOne);
  };
  const doInsights = () => {
    if (selectedOne && isSentSelected) onInsightsCampaign(selectedOne);
  };
  const doDelete = () => {
    const hasSent = selected.some((c) => c.status.toLowerCase() === "sent");
    if (selected.length > 0 && !hasSent) {
      onDeleteCampaigns(selected);
    }
    setSelectedIds([]);
  };

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <Paper sx={{ mb: 2 }}>
        <Toolbar>
          <Typography sx={{ flex: "1 1 100%" }} variant="h6">
            Campaigns
          </Typography>
          <TextField
            size="small"
            placeholder="Search…"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
            sx={{ width: 240, mr: 2 }}
          />
          <Button
            onClick={doEdit}
            disabled={!selectedOne || isSentSelected}
            startIcon={<EditIcon />}
            sx={{ mr: 1 }}
          >
            Edit
          </Button>
          <Button
            onClick={isScheduledSelected ? undefined : doSend}
            disabled={!selectedOne || isSentSelected || isScheduledSelected}
            startIcon={isScheduledSelected ? <ScheduleIcon /> : <PlayArrowIcon />}
            sx={{ mr: 1 }}
          >
            {isScheduledSelected ? "Scheduled" : "Run"}
          </Button>

          <Button
            onClick={doInsights}
            disabled={!selectedOne || !isSentSelected}
            startIcon={<InsightsIcon />}
            sx={{ mr: 1 }}
          >
            Insights
          </Button>
          <Tooltip
            title={
              selected.some((c) => c.status.toLowerCase() === "sent")
                ? "Cannot delete sent campaigns"
                : "Delete"
            }
          >
            <span>
              <IconButton
                onClick={doDelete}
                disabled={
                  selected.length === 0 ||
                  selected.some((c) => c.status.toLowerCase() === "sent")
                }
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Toolbar>

        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selected.length > 0 && selected.length < filtered.length
                    }
                    checked={
                      filtered.length > 0 && selected.length === filtered.length
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Template</TableCell>
                <TableCell>Audience / Run at</TableCell>
                <TableCell>Schedule Mode</TableCell>
                <TableCell>Scheduled (UTC)</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((c) => {
                  const id = c.campaign_id;
                  const isSelected = selectedIds.includes(id);
                  const sent = c.status.toLowerCase() === "sent";
                  return (
                    <TableRow
                      hover
                      key={id}
                      selected={isSelected}
                      onClick={() => handleSelectOne(id)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox checked={isSelected} />
                      </TableCell>
                      <TableCell>{c.campaign_name}</TableCell>
                      <TableCell>{c.template_name || c.template?.template_name || "-"}</TableCell>
                      <TableCell>
                        {sent ? (
                          c.last_run_at ? new Date(c.last_run_at).toLocaleString() : "–"
                        ) : (
                          <>
                            {c.audience ? (
                              <>
                                {c.audience.type === "subscribed"
                                  ? "All subscribers"
                                  : c.audience.type === "csv"
                                  ? "CSV"
                                  : c.audience.type}
                                {c.audience.type !== "subscribed" && c.audience.count != null && (
                                  <>
                                    {" | "}
                                    {`~${c.audience.count} recipient${
                                      c.audience.count === 1 ? "" : "s"
                                    }`}
                                  </>
                                )}
                              </>
                            ) : (
                              "–"
                            )}
                          </>
                        )}
                      </TableCell>
                      <TableCell>{prettyMode(c.schedule_mode)}</TableCell>
                      <TableCell>
                        {c.schedule_run_at_utc ? formatUtc(c.schedule_run_at_utc) : "-"}
                      </TableCell>
                      <TableCell>{c.status.charAt(0).toUpperCase() + c.status.slice(1).toLowerCase()}</TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, np) => setPage(np)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(+e.target.value);
            setPage(0);
          }}
        />
      </Paper>
    </Box>
  );
});
